import React, { useState, useEffect } from "react";
import { X, Eye, EyeOff, Check, LogOut } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useModal } from "../../hooks/useModal";
import { ApiService } from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UserProfilePanel = ({ isOpen, onClose, isHomePage = false }: { isOpen: boolean; onClose?: () => void; isHomePage?: boolean }) => {
  const { user, logout } = useAuth();
  const { showAlert } = useModal();
  const { currentTheme } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const panelPositionClass = isMobile ? "inset-0 top-[60px]" : "top-[60px] right-0 bottom-0 w-80";
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileForm>({
    firstName: "",
    lastName: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [meData, setMeData] = useState<any>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isOpen) return;
      try {
        const me = await ApiService.getMe();
        setMeData(me || null);
        setProfileForm({
          firstName: me?.firstName || "",
          lastName: me?.lastName || "",
          email: me?.email || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } catch (error) {
        if (user) {
          setProfileForm({
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        }
      }
    };
    fetchProfile();
  }, [isOpen, user]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors: string[] = [];

    if (profileForm.newPassword || profileForm.confirmPassword || profileForm.currentPassword) {
      if (!profileForm.currentPassword) {
        errors.push("Şifre değiştirmek için mevcut şifrenizi giriniz");
      }

      if (profileForm.newPassword.length < 8) {
        errors.push("Yeni şifre en az 8 karakter olmalıdır");
      }

      if (profileForm.newPassword !== profileForm.confirmPassword) {
        errors.push("Yeni şifreler eşleşmiyor");
      }
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      await showAlert({
        title: "Doğrulama Hatası",
        message: errors.join("\\n"),
        type: "error",
      });
      return;
    }

    setIsSaving(true);
    setIsSaved(false);

    try {
      if (profileForm.currentPassword || profileForm.newPassword || profileForm.confirmPassword) {
        await ApiService.changePassword({
          currentPassword: profileForm.currentPassword,
          newPassword: profileForm.newPassword,
          confirmPassword: profileForm.confirmPassword,
        });
      } else {
        setIsSaving(false);
        return;
      }

      setIsSaving(false);
      setIsSaved(true);

      setProfileForm((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      setTimeout(() => {
        setIsSaved(false);
        onClose?.();
      }, 2000);
    } catch (error: any) {
      setIsSaving(false);
      const message = error?.message || "Şifre değiştirilirken bir hata oluştu.";
      await showAlert({ title: "Hata", message, type: "error" });
    }
  };

  const handleLogout = () => {
    logout();
    onClose?.();
  };

  const meta = meData?.metadata || {};
  const birthDate = meData?.birthDate || null;
  const hasAnyMeta = Boolean(
    birthDate ||
    (typeof meta.schoolNumber === "string" && meta.schoolNumber) ||
    (typeof meta.tcNumber === "string" && meta.tcNumber) ||
    (typeof meta.parentPhone === "string" && meta.parentPhone) ||
    (typeof meta.parentEmail === "string" && meta.parentEmail)
  );

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .profile-container::-webkit-scrollbar {
            display: none;
          }
          .profile-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .homepage-placeholder {
            background: transparent !important;
          }
          .homepage-placeholder::placeholder {
            color: rgba(255, 255, 255, 0.9) !important;
            opacity: 1 !important;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes checkmark {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }

          .spinning {
            animation: spin 1s linear infinite;
          }

          .checkmark-animation {
            animation: checkmark 0.6s ease-out;
          }

        `}
      </style>

      {isMobile && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onClose} />}

      {!isHomePage && <div className={`fixed z-49 bg-white ${panelPositionClass}`} />}

      <div className={`fixed z-50 ${panelPositionClass}`}>
        <div
          className={`flex flex-col h-full shadow-2xl w-full ${isHomePage ? "backdrop-blur-lg" : "bg-white"} ${isMobile ? "rounded-none p-0" : isHomePage ? "border border-white/25 p-1" : "border border-gray-200 p-1"
            }`}
          style={
            isHomePage
              ? {
                background: `linear-gradient(135deg, ${currentTheme.colors.background.base}26, ${currentTheme.colors.background.via}33, ${currentTheme.colors.background.to}26)`,
              }
              : undefined
          }>
          <div className="flex-shrink-0 px-6" style={{ paddingTop: "11px", paddingBottom: "11px" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                    {meData?.media?.url ? (
                      <img src={meData.media.url} alt={`${user?.firstName} ${user?.lastName}`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-medium">
                        {user?.firstName?.charAt(0)}
                        {user?.lastName?.charAt(0)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <h2 className={`text-sm font-semibold ${isHomePage ? "text-white" : "text-gray-900"}`}>
                    {user?.firstName} {user?.lastName}
                  </h2>
                  <p className={`text-xs ${isHomePage ? "text-gray-200" : "text-gray-500"}`}>
                    {user?.department?.name || "Departman bilgisi yok"}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleLogout}
                  className={`p-1.5 rounded transition-colors ${isHomePage ? "text-gray-200 hover:text-red-300 hover:bg-red-500/20" : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                    }`}
                  title="Çıkış Yap"
                  aria-label="Çıkış Yap">
                  <LogOut size={16} />
                </button>
                {onClose && (
                  <button
                    onClick={onClose}
                    className={`p-1.5 rounded transition-colors ${isHomePage
                      ? "text-gray-200 hover:text-white hover:bg-white/15"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      }`}
                    aria-label="Kapat">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="profile-container flex-1 overflow-y-auto px-6 py-4 space-y-5">
            <div>
              <h3 className={`text-sm font-semibold mb-3 ${isHomePage ? "text-white" : "text-gray-900"}`}>Kişisel Bilgiler</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>Ad *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={profileForm.firstName}
                    readOnly
                    className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                      ? "bg-transparent border-white/40 text-white placeholder-gray-100 homepage-placeholder"
                      : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    placeholder="Ahmet"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>Soyad *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={profileForm.lastName}
                    readOnly
                    className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                      ? "bg-transparent border-white/40 text-white placeholder-gray-100 homepage-placeholder"
                      : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    placeholder="Yılmaz"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className={`text-sm font-semibold mb-3 ${isHomePage ? "text-white" : "text-gray-900"}`}>İletişim Bilgileri</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    value={profileForm.email}
                    readOnly
                    className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                      ? "bg-transparent border-white/40 text-white placeholder-gray-100 homepage-placeholder"
                      : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    placeholder="ahmet.yilmaz@gmail.com"
                  />
                </div>
              </div>
            </div>

            {hasAnyMeta && (
              <div>
                <h3 className={`text-sm font-semibold mb-3 ${isHomePage ? "text-white" : "text-gray-900"}`}>Ek Bilgiler</h3>
                <div className="grid grid-cols-2 gap-4">
                  {birthDate && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                        Doğum Tarihi
                      </label>
                      <input
                        type="text"
                        value={new Date(birthDate).toLocaleDateString("tr-TR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                        readOnly
                        className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                          ? "bg-transparent border-white/40 text-white placeholder-gray-100"
                          : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                      />
                    </div>
                  )}
                  {meta.schoolNumber && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                        Okul Numarası
                      </label>
                      <input
                        type="text"
                        value={meta.schoolNumber}
                        readOnly
                        className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                          ? "bg-transparent border-white/40 text-white placeholder-gray-100"
                          : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                      />
                    </div>
                  )}
                  {meta.tcNumber && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                        TC Kimlik No
                      </label>
                      <input
                        type="text"
                        value={meta.tcNumber}
                        readOnly
                        className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                          ? "bg-transparent border-white/40 text-white placeholder-gray-100"
                          : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                      />
                    </div>
                  )}
                  {meta.parentPhone && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                        Veli Telefonu
                      </label>
                      <input
                        type="text"
                        value={meta.parentPhone}
                        readOnly
                        className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                          ? "bg-transparent border-white/40 text-white placeholder-gray-100"
                          : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                      />
                    </div>
                  )}
                  {meta.parentEmail && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                        Veli E-posta
                      </label>
                      <input
                        type="text"
                        value={meta.parentEmail}
                        readOnly
                        className={`w-full px-0 py-2 border-0 border-b-2 rounded-none focus:outline-none cursor-not-allowed ${isHomePage
                          ? "bg-transparent border-white/40 text-white placeholder-gray-100"
                          : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h3 className={`text-sm font-semibold mb-3 ${isHomePage ? "text-white" : "text-gray-900"}`}>Şifre Değiştirme</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>Mevcut Şifre</label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      name="currentPassword"
                      value={profileForm.currentPassword}
                      onChange={handleFormChange}
                      className={`w-full px-0 py-2 pr-8 border-0 border-b-2 rounded-none focus:outline-none focus:ring-0 focus:border-blue-500 ${isHomePage
                        ? "bg-transparent border-white/40 text-white placeholder-gray-100 homepage-placeholder"
                        : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      placeholder="Mevcut Şifreniz"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className={`absolute right-0 top-1/2 -translate-y-1/2 transition-colors ${isHomePage ? "text-gray-200 hover:text-white" : "text-gray-400 hover:text-gray-600"
                        }`}>
                      {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>Yeni Şifre</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      value={profileForm.newPassword}
                      onChange={handleFormChange}
                      className={`w-full px-0 py-2 pr-8 border-0 border-b-2 rounded-none focus:outline-none focus:ring-0 focus:border-blue-500 ${isHomePage
                        ? "bg-transparent border-white/40 text-white placeholder-gray-100 homepage-placeholder"
                        : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      placeholder="En Az 8 Karakter"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className={`absolute right-0 top-1/2 -translate-y-1/2 transition-colors ${isHomePage ? "text-gray-200 hover:text-white" : "text-gray-400 hover:text-gray-600"
                        }`}>
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isHomePage ? "text-gray-200" : "text-gray-700"}`}>
                    Yeni Şifre Tekrar
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={profileForm.confirmPassword}
                      onChange={handleFormChange}
                      className={`w-full px-0 py-2 pr-8 border-0 border-b-2 rounded-none focus:outline-none focus:ring-0 focus:border-blue-500 ${isHomePage
                        ? "bg-transparent border-white/40 text-white placeholder-gray-100 homepage-placeholder"
                        : "bg-transparent border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      placeholder="Şifrenizi Tekrar Yazın"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className={`absolute right-0 top-1/2 -translate-y-1/2 transition-colors ${isHomePage ? "text-gray-200 hover:text-white" : "text-gray-400 hover:text-gray-600"
                        }`}>
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-6 py-4">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className={`flex-1 px-4 py-2 border rounded-lg transition-colors font-medium ${isHomePage
                  ? "text-white bg-white/15 border-white/25 hover:bg-white/20 backdrop-blur-sm"
                  : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
                  }`}>
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isSaved}
                className={`flex-1 px-4 py-2 rounded-lg transition-all duration-200 font-medium flex items-center justify-center space-x-2 ${isSaved
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : isSaving
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}>
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-isHomePage rounded-full spinning"></div>
                    <span>Kaydediliyor...</span>
                  </>
                ) : isSaved ? (
                  <>
                    <Check size={16} className="checkmark-animation" />
                    <span>Kaydedildi!</span>
                  </>
                ) : (
                  <span>Kaydet</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfilePanel;
